import { type RefObject, useEffect } from "react"

import { focusFirstRefusal } from "@/lib/refusal-focus"

/**
 * OKS-1006 D3 — move focus to the refusal once per response, AFTER the render
 * that shows it has committed.
 *
 * Keyed on the `<Form>` render-prop `errors` object. In `@inertiajs/react` that
 * object is `useForm`'s `useState`, so its identity changes exactly once per
 * server response: the effect fires once per refusal, never on a keystroke,
 * and is a no-op on the `{}` a success leaves behind. Because it is an effect,
 * it runs after the DOM carries everything the errored render mounted — the
 * `aria-invalid` control, the opened « Plus de détails », the dialog-level
 * message — which is the ordering an `onError` callback cannot give (see
 * {@link focusFirstRefusal}).
 *
 * @param errors the render-prop errors bag (any value shape).
 * @param containerRef the form body to search; only this dialog's controls.
 */
export function useFocusOnRefusal(
  errors: Record<string, unknown> | undefined,
  containerRef: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    if (!errors || Object.keys(errors).length === 0) return
    focusFirstRefusal(containerRef.current)
  }, [errors, containerRef])
}
